import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { ChevronRight, MessageSquare, Send, Car, Check, Clock, User, Bot, Image, X } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useSuperAdminAuth } from '@/contexts/SuperAdminAuthContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

interface OilRequest {
  id: string;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  car_make: string;
  car_model: string;
  car_year: string;
  engine_type: string;
  status: 'pending' | 'responded' | 'completed';
  customer_message?: string;
  admin_response?: string;
  created_at: string;
  updated_at?: string;
  user_id: string;
}

interface Message {
  id: string;
  oil_request_id: string;
  sender_type: 'customer' | 'admin';
  message: string;
  message_type: 'text' | 'image';
  image_url?: string;
  created_at: string;
}

const OilRequestAdmin = () => {
  const { user, loading: authLoading } = useSuperAdminAuth();
  const [requests, setRequests] = useState<OilRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<OilRequest | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [lastReadMessages, setLastReadMessages] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      fetchRequests();
      // Load last read messages from localStorage
      const saved = localStorage.getItem('oil-request-admin-last-read');
      if (saved) {
        setLastReadMessages(JSON.parse(saved));
      }
    }
  }, [user, authLoading]);

  useEffect(() => {
    if (selectedRequest) {
      fetchMessages(selectedRequest.id);
      // Mark as read when selecting a request
      markRequestAsRead(selectedRequest.id);
    }
  }, [selectedRequest]);

  if (!authLoading && !user) {
    return <Navigate to="/super-admin-login" replace />;
  }

  const markRequestAsRead = (requestId: string) => {
    const now = new Date().toISOString();
    const updatedLastRead = { ...lastReadMessages, [requestId]: now };
    setLastReadMessages(updatedLastRead);
    localStorage.setItem('oil-request-admin-last-read', JSON.stringify(updatedLastRead));
  };

  const hasUnreadMessages = (request: OilRequest) => {
    const lastRead = lastReadMessages[request.id];
    if (!lastRead) return request.status === 'pending';
    
    // Check if the request was updated after last read
    return request.updated_at && new Date(request.updated_at) > new Date(lastRead);
  };

  const fetchRequests = async () => {
    try {
      const { data, error } = await supabase
        .from('oil_requests')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      const typedData = (data || []).map(item => ({
        ...item,
        status: item.status as 'pending' | 'responded' | 'completed'
      }));
      
      setRequests(typedData);
    } catch (error) {
      console.error('Error fetching requests:', error);
      toast.error('فشل في تحميل الطلبات');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (requestId: string) => {
    try {
      const { data, error } = await supabase
        .from('oil_request_messages')
        .select('*')
        .eq('oil_request_id', requestId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const typedMessages = (data || []).map(message => ({
        ...message,
        sender_type: message.sender_type as 'customer' | 'admin',
        message_type: (message.message_type as 'text' | 'image') || 'text'
      }));

      setMessages(typedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast.error('فشل في تحميل الرسائل');
    }
  };

  const handleStatusUpdate = async (requestId: string, newStatus: 'responded' | 'completed') => {
    try {
      const { error } = await supabase
        .from('oil_requests')
        .update({ 
          status: newStatus,
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      setRequests(prev => 
        prev.map(req => 
          req.id === requestId 
            ? { ...req, status: newStatus, updated_at: new Date().toISOString() }
            : req
        )
      );

      toast.success('تم تحديث حالة الطلب بنجاح');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('فشل في تحديث حالة الطلب');
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        toast.error('حجم الصورة يجب أن يكون أقل من 5 ميجابايت');
        return;
      }
      
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const uploadImage = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
    
    const { data, error } = await supabase.storage
      .from('oil-request-images')
      .upload(fileName, file);

    if (error) throw error;

    const { data: urlData } = supabase.storage
      .from('oil-request-images')
      .getPublicUrl(fileName);

    return urlData.publicUrl;
  };

  const handleResponseSubmit = async () => {
    if (!selectedRequest || (!response.trim() && !selectedImage)) return;

    setSubmitting(true);

    try {
      let imageUrl = null;
      
      // Upload image if selected
      if (selectedImage) {
        imageUrl = await uploadImage(selectedImage);
      }

      // Send message
      const messageData = {
        oil_request_id: selectedRequest.id,
        sender_type: 'admin',
        message: response.trim() || (imageUrl ? 'صورة' : ''),
        message_type: imageUrl ? 'image' : 'text',
        ...(imageUrl && { image_url: imageUrl })
      };

      const { error } = await supabase
        .from('oil_request_messages')
        .insert(messageData);

      if (error) throw error;

      // Update the request status
      await supabase
        .from('oil_requests')
        .update({ 
          status: 'responded',
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedRequest.id);

      setRequests(prev => 
        prev.map(req => 
          req.id === selectedRequest.id 
            ? { 
                ...req, 
                status: 'responded' as const,
                updated_at: new Date().toISOString()
              }
            : req
        )
      );

      setResponse('');
      clearSelectedImage();
      fetchMessages(selectedRequest.id);
      toast.success('تم إرسال الرد بنجاح');
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error('فشل في إرسال الرد');
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">في الانتظار</Badge>;
      case 'responded':
        return <Badge variant="secondary" className="bg-blue-100 text-blue-800">تم الرد</Badge>;
      case 'completed':
        return <Badge variant="secondary" className="bg-green-100 text-green-800">مكتمل</Badge>;
      default:
        return <Badge variant="secondary">غير معروف</Badge>;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('ar-SA', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-autocare-blue mx-auto mb-4"></div>
          <p>جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      
      <div className="flex-grow pt-20 pb-12">
        <div className="page-container max-w-6xl">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-autocare-blue mb-2">إدارة طلبات الزيوت</h1>
            <p className="text-gray-600">إدارة والرد على طلبات العملاء للحصول على توصيات الزيوت</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Requests List */}
            <div>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    قائمة الطلبات ({requests.length})
                  </CardTitle>
                  <CardDescription>
                    جميع طلبات العملاء مرتبة حسب التاريخ
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {requests.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">لا توجد طلبات حالياً</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {requests.map((request) => (
                        <div
                          key={request.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-colors relative ${
                            selectedRequest?.id === request.id
                              ? 'border-autocare-blue bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => setSelectedRequest(request)}
                        >
                          {hasUnreadMessages(request) && (
                            <div className="absolute top-2 right-2 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                          )}
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="font-semibold text-gray-900">{request.customer_name}</h3>
                              <p className="text-sm text-gray-600">{request.customer_phone}</p>
                            </div>
                            <div className="flex items-center gap-2">
                              {hasUnreadMessages(request) && (
                                <Badge variant="destructive" className="text-xs px-1 py-0 h-5">
                                  جديد
                                </Badge>
                              )}
                              {getStatusBadge(request.status)}
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                            <Car className="h-4 w-4" />
                            <span>{request.car_make} {request.car_model} ({request.car_year})</span>
                          </div>
                          
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-500">
                              {formatDate(request.created_at)}
                            </span>
                            <ChevronRight className="h-4 w-4 text-gray-400" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Chat Interface */}
            <div>
              {selectedRequest ? (
                <Card className="h-[700px] flex flex-col">
                  <CardHeader className="border-b">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Car className="h-5 w-5" />
                          {selectedRequest.car_make} {selectedRequest.car_model} ({selectedRequest.car_year})
                        </CardTitle>
                        <CardDescription>
                          العميل: {selectedRequest.customer_name} - {selectedRequest.customer_phone}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        {getStatusBadge(selectedRequest.status)}
                        {selectedRequest.status !== 'completed' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleStatusUpdate(selectedRequest.id, 'completed')}
                          >
                            <Check className="h-4 w-4 mr-1" />
                            إكمال
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>

                  {/* Messages Area */}
                  <CardContent className="flex-1 overflow-y-auto p-4">
                    <div className="space-y-4">
                      {/* Initial request message */}
                      <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <User className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="bg-blue-50 rounded-lg p-3">
                            <p className="text-sm mb-2">
                              <strong>طلب جديد للسيارة:</strong>
                            </p>
                            <p className="text-sm">
                              {selectedRequest.car_make} {selectedRequest.car_model} ({selectedRequest.car_year})
                              <br />
                              نوع المحرك: {selectedRequest.engine_type}
                            </p>
                            {selectedRequest.customer_message && (
                              <p className="text-sm mt-2 border-t pt-2">
                                <strong>رسالة إضافية:</strong> {selectedRequest.customer_message}
                              </p>
                            )}
                          </div>
                          <span className="text-xs text-gray-500 mt-1 block">
                            {formatTime(selectedRequest.created_at)}
                          </span>
                        </div>
                      </div>

                      {/* Chat messages */}
                      {messages.map((message) => (
                        <div key={message.id} className={`flex gap-3 ${message.sender_type === 'admin' ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            message.sender_type === 'customer' 
                              ? 'bg-blue-100' 
                              : 'bg-green-100'
                          }`}>
                            {message.sender_type === 'customer' ? (
                              <User className="h-4 w-4 text-blue-600" />
                            ) : (
                              <Bot className="h-4 w-4 text-green-600" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className={`rounded-lg p-3 ${
                              message.sender_type === 'customer' 
                                ? 'bg-blue-50' 
                                : 'bg-green-50'
                            }`}>
                              {message.message_type === 'image' && message.image_url ? (
                                <div className="mb-2">
                                  <img 
                                    src={message.image_url} 
                                    alt="صورة مرسلة" 
                                    className="max-w-xs rounded-lg cursor-pointer"
                                    onClick={() => window.open(message.image_url, '_blank')}
                                  />
                                  {message.message && message.message !== 'صورة' && (
                                    <p className="text-sm mt-2">{message.message}</p>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm">{message.message}</p>
                              )}
                            </div>
                            <span className="text-xs text-gray-500 mt-1 block">
                              {formatTime(message.created_at)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>

                  {/* Response Input */}
                  <div className="border-t p-4">
                    <div className="space-y-3">
                      {/* Image Preview */}
                      {imagePreview && (
                        <div className="relative inline-block">
                          <img 
                            src={imagePreview} 
                            alt="معاينة الصورة" 
                            className="max-w-32 max-h-32 rounded-lg"
                          />
                          <Button
                            variant="destructive"
                            size="icon"
                            className="absolute -top-2 -right-2 h-6 w-6 rounded-full"
                            onClick={clearSelectedImage}
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      )}
                      
                      <Textarea
                        placeholder="اكتب ردك على طلب العميل..."
                        value={response}
                        onChange={(e) => setResponse(e.target.value)}
                        rows={3}
                      />
                      
                      <div className="flex gap-2">
                        <Input
                          type="file"
                          accept="image/*"
                          onChange={handleImageSelect}
                          className="hidden"
                          id="image-upload"
                        />
                        <label htmlFor="image-upload">
                          <Button variant="outline" asChild>
                            <span>
                              <Image className="h-4 w-4 mr-2" />
                              إرفاق صورة
                            </span>
                          </Button>
                        </label>
                        
                        <Button
                          onClick={handleResponseSubmit}
                          disabled={submitting || (!response.trim() && !selectedImage)}
                          className="flex-1"
                        >
                          <Send className="h-4 w-4 mr-2" />
                          {submitting ? 'جاري الإرسال...' : 'إرسال الرد'}
                        </Button>
                      </div>
                    </div>

                    {/* Request Metadata */}
                    <div className="pt-3 mt-3 border-t border-gray-200">
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <Clock className="h-4 w-4" />
                        <span>تاريخ الإنشاء: {formatDate(selectedRequest.created_at)}</span>
                      </div>
                      {selectedRequest.updated_at && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                          <Clock className="h-4 w-4" />
                          <span>آخر تحديث: {formatDate(selectedRequest.updated_at)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ) : (
                <Card>
                  <CardContent className="flex items-center justify-center h-64">
                    <div className="text-center text-gray-500">
                      <MessageSquare className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>حدد طلباً من القائمة لعرض المحادثة</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default OilRequestAdmin;
